import { useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import type { SignupAnalyticsContext, SignupFlowSource } from "../analytics/signup";
import { sanitizeAnalyticsUrl, sanitizeNestedPathOrUrl } from "../analytics/sanitizeAnalyticsUrl";
import { resolveQrSessionCorrelation } from "../utils/qrSessionCorrelation";

const SOURCE_VALUES: SignupFlowSource[] = [
  "direct",
  "qr",
  "invite",
  "landing",
  "internal_navigation",
];

const isSignupFlowSource = (value: string | null): value is SignupFlowSource =>
  !!value && SOURCE_VALUES.includes(value as SignupFlowSource);

const resolveSource = (
  explicitSource: string | null,
  inviteCode: string | undefined,
  hasQrSignals: boolean,
): SignupFlowSource => {
  if (isSignupFlowSource(explicitSource)) return explicitSource;
  if (inviteCode) return "invite";
  if (hasQrSignals) return "qr";

  if (typeof document === "undefined") return "direct";
  if (!document.referrer) return "direct";

  try {
    const referrer = new URL(document.referrer);
    const sameOrigin = referrer.origin === window.location.origin;

    if (sameOrigin && (referrer.pathname.startsWith("/qr") || referrer.pathname.startsWith("/r/"))) {
      return "qr";
    }

    if (sameOrigin) return "internal_navigation";
    return "landing";
  } catch {
    return "landing";
  }
};

export const useSignupAnalyticsContext = (): SignupAnalyticsContext => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const utmSource = searchParams.get("utm_source") ?? undefined;
    const utmCampaign = searchParams.get("utm_campaign") ?? undefined;
    const variant = searchParams.get("variant") ?? undefined;
    const returnTo =
      searchParams.get("return_to") ?? searchParams.get("returnTo") ?? undefined;
    const inviteCode =
      searchParams.get("inviteCode") ?? searchParams.get("code") ?? undefined;

    const qrSessionCorrelation = resolveQrSessionCorrelation();
    const source = resolveSource(
      searchParams.get("source"),
      inviteCode,
      !!utmSource || !!utmCampaign || !!variant,
    );

    return {
      source,
      return_to: returnTo ? sanitizeNestedPathOrUrl(returnTo) : undefined,
      invite_code: inviteCode,
      utm_source: utmSource,
      utm_campaign: utmCampaign,
      variant,
      qr_session_id: qrSessionCorrelation.posthogProperties.qr_session_id,
      page_url:
        typeof window !== "undefined"
          ? sanitizeAnalyticsUrl(
              `${window.location.origin}${location.pathname}${location.search}`,
            )
          : undefined,
      context_timestamp: new Date().toISOString(),
    };
  }, [location.pathname, location.search, searchParams]);
};
